
import { Link } from 'react-router-dom';

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-gray-900 text-white py-6 sticky top-0 z-50 shadow-lg">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 cursor-pointer">
            <img
              src="https://static.readdy.ai/image/3ca3a64fdbdd1a27f61611d5a6ab11d4/0b305659fb07b7246329e00bf7bbf5b5.png"
              alt="VAS Cyber"
              className="w-12 h-12"
            />
            <span className="text-xl font-bold">VAS Cyber</span>
          </Link>
          <Link
            to="/"
            className="px-6 py-2 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold rounded-full hover:shadow-lg hover:shadow-emerald-500/50 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            Back to Home
          </Link>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-block bg-emerald-500 text-white text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-full mb-6">
            Legal Information
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">Privacy Policy</h1>
          <p className="text-xl text-gray-300">
            Your privacy and data security are our top priorities
          </p>
          <p className="text-sm text-gray-400 mt-4">Last Updated: January 2025</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="prose prose-lg max-w-none">
            {/* Introduction */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <i className="ri-information-line text-emerald-500"></i>
                Introduction
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                VAS Cyber ("we," "our," or "us") is committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
              </p>
              <p className="text-gray-700 leading-relaxed">
                By accessing or using our services, you agree to the terms of this Privacy Policy. If you do not agree with our policies and practices, please do not use our services.
              </p>
            </div>

            {/* Information We Collect */}
            <div className="mb-12 bg-gray-50 rounded-2xl p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <i className="ri-database-2-line text-emerald-500"></i>
                Information We Collect
              </h2>
              
              <h3 className="text-xl font-bold text-gray-900 mb-3">Personal Information</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                We may collect personal information that you voluntarily provide to us, including:
              </p>
              <ul className="space-y-2 mb-6">
                {[
                  'Name and contact information (email, phone number, address)',
                  'Company name and job title',
                  'Professional credentials and certifications',
                  'Payment and billing information',
                  'Communication preferences',
                  'Any other information you choose to provide'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3 text-gray-700">
                    <i className="ri-checkbox-circle-fill text-emerald-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <h3 className="text-xl font-bold text-gray-900 mb-3">Technical Information</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                We automatically collect certain technical information when you visit our website:
              </p>
              <ul className="space-y-2">
                {[
                  'IP address and device information',
                  'Browser type and version',
                  'Operating system',
                  'Pages visited and time spent on pages',
                  'Referring website addresses',
                  'Cookies and similar tracking technologies'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3 text-gray-700">
                    <i className="ri-checkbox-circle-fill text-emerald-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* How We Use Your Information */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <i className="ri-settings-3-line text-emerald-500"></i>
                How We Use Your Information
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We use the information we collect for the following purposes:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  'Provide and maintain our services',
                  'Process transactions and send confirmations',
                  'Respond to inquiries and provide support',
                  'Send service updates and security alerts',
                  'Improve our website and services',
                  'Conduct security assessments and audits',
                  'Comply with legal obligations',
                  'Prevent fraud and unauthorized access'
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3 bg-white rounded-lg p-4 border-l-4 border-emerald-500">
                    <i className="ri-arrow-right-s-line text-emerald-500 text-xl flex-shrink-0"></i>
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Data Security */}
            <div className="mb-12 bg-gradient-to-br from-emerald-50 to-cyan-50 rounded-2xl p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <i className="ri-shield-check-line text-emerald-500"></i>
                Data Security
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                As a cybersecurity company, we implement industry-leading security measures to protect your information:
              </p>
              <ul className="space-y-3">
                {[
                  'End-to-end encryption for data transmission',
                  'Secure data storage with access controls',
                  'Regular security audits and vulnerability assessments',
                  'Employee training on data protection',
                  'Incident response procedures',
                  'Compliance with international security standards'
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3 text-gray-700">
                    <i className="ri-shield-check-fill text-emerald-500 text-xl flex-shrink-0 mt-0.5"></i>
                    <span className="font-medium">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Information Sharing */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <i className="ri-share-line text-emerald-500"></i>
                Information Sharing and Disclosure
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We do not sell, trade, or rent your personal information. We may share your information only in the following circumstances:
              </p>
              <div className="space-y-4">
                <div className="bg-white rounded-lg p-6 border-l-4 border-emerald-500">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Service Providers</h3>
                  <p className="text-gray-700">
                    With trusted third-party service providers who assist in operating our business, subject to strict confidentiality agreements.
                  </p>
                </div>
                <div className="bg-white rounded-lg p-6 border-l-4 border-cyan-500">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Legal Requirements</h3>
                  <p className="text-gray-700">
                    When required by law, court order, or government regulation, or to protect our rights and safety.
                  </p>
                </div>
                <div className="bg-white rounded-lg p-6 border-l-4 border-gray-500">
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Business Transfers</h3>
                  <p className="text-gray-700">
                    In connection with a merger, acquisition, or sale of assets, with notice to affected users.
                  </p>
                </div>
              </div>
            </div>

            {/* Your Rights */}
            <div className="mb-12 bg-gray-50 rounded-2xl p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <i className="ri-user-settings-line text-emerald-500"></i>
                Your Rights and Choices
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                You have the following rights regarding your personal information:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { title: 'Access', desc: 'Request access to your personal data' },
                  { title: 'Correction', desc: 'Request correction of inaccurate data' },
                  { title: 'Deletion', desc: 'Request deletion of your data' },
                  { title: 'Portability', desc: 'Request transfer of your data' },
                  { title: 'Opt-Out', desc: 'Opt-out of marketing communications' },
                  { title: 'Restriction', desc: 'Request restriction of processing' }
                ].map((right, index) => (
                  <div key={index} className="bg-white rounded-lg p-4 border border-gray-200">
                    <h3 className="text-lg font-bold text-gray-900 mb-1">{right.title}</h3>
                    <p className="text-sm text-gray-600">{right.desc}</p>
                  </div>
                ))}
              </div>
              <p className="text-gray-700 leading-relaxed mt-6">
                To exercise these rights, please contact us using the information provided below.
              </p>
            </div>

            {/* Cookies */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <i className="ri-cookie-line text-emerald-500"></i>
                Cookies and Tracking Technologies
              </h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We use cookies and similar tracking technologies to enhance your experience on our website. You can control cookie preferences through your browser settings.
              </p>
              <div className="bg-cyan-50 rounded-lg p-6 border-l-4 border-cyan-500">
                <p className="text-gray-700">
                  <strong>Note:</strong> Disabling cookies may limit your ability to use certain features of our website.
                </p>
              </div>
            </div>

            {/* Data Retention */}
            <div className="mb-12 bg-gray-50 rounded-2xl p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <i className="ri-time-line text-emerald-500"></i>
                Data Retention
              </h2>
              <p className="text-gray-700 leading-relaxed">
                We retain your personal information only for as long as necessary to fulfill the purposes outlined in this Privacy Policy, comply with legal obligations, resolve disputes, and enforce our agreements. When information is no longer needed, we securely delete or anonymize it.
              </p>
            </div>

            {/* International Transfers */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <i className="ri-global-line text-emerald-500"></i>
                International Data Transfers
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Your information may be transferred to and processed in countries other than Ghana. We ensure that such transfers comply with applicable data protection laws and implement appropriate safeguards to protect your information.
              </p>
            </div>

            {/* Children's Privacy */}
            <div className="mb-12 bg-gradient-to-br from-gray-50 to-slate-50 rounded-2xl p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <i className="ri-parent-line text-emerald-500"></i>
                Children's Privacy
              </h2>
              <p className="text-gray-700 leading-relaxed">
                Our services are not directed to individuals under the age of 18. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately.
              </p>
            </div>

            {/* Changes to Policy */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                <i className="ri-refresh-line text-emerald-500"></i>
                Changes to This Privacy Policy
              </h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. Your continued use of our services after such changes constitutes acceptance of the updated policy.
              </p>
            </div>

            {/* Contact */}
            <div className="bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-2xl p-8 text-white">
              <h2 className="text-3xl font-bold mb-4 flex items-center gap-3">
                <i className="ri-mail-line"></i>
                Contact Us
              </h2>
              <p className="text-lg mb-6">
                If you have any questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="space-y-3">
                <p className="flex items-center gap-3">
                  <i className="ri-building-line text-xl"></i>
                  <strong>VAS Cyber</strong>
                </p>
                <p className="flex items-center gap-3">
                  <i className="ri-map-pin-line text-xl"></i>
                  Ghana
                </p>
                <p className="flex items-center gap-3">
                  <i className="ri-mail-line text-xl"></i>
                  privacy@vascyber.com
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 border-t border-gray-800 pt-8">
            <p className="text-sm text-gray-400">
              © 2025 VAS Cyber. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link
                to="/privacy"
                className="text-sm text-gray-400 hover:text-emerald-400 transition-colors duration-300 cursor-pointer"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms"
                className="text-sm text-gray-400 hover:text-emerald-400 transition-colors duration-300 cursor-pointer"
              >
                Terms of Service
              </Link>
              <a
                href="https://readdy.ai/?origin=logo"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-gray-400 hover:text-emerald-400 transition-colors duration-300 cursor-pointer"
              >
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
